// StatusModal.jsx
import React from "react";
import {
  Dialog,
  DialogContent,
  Slide,
  Alert,
  AlertTitle,
  IconButton,
  Box,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - severity: 'success' | 'info' | 'warning' | 'error'
 * - message: string | ReactNode
 * - title?: string
 */
export default function StatusModal({
  open,
  onClose,
  severity = "success",
  message,
  title,
}) {
  const theme = useTheme();

  const paletteKey = severity; // matches MUI keys
  const color = theme.palette[paletteKey].main;
  const bg = alpha(color, 0.12);

  const titleMap = {
    success: "Success",
    info: "Notice",
    warning: "Heads up",
    error: "Error",
  };

  const Icon =
    severity === "success"
      ? CheckCircleRoundedIcon
      : severity === "error"
      ? ErrorOutlineRoundedIcon
      : severity === "warning"
      ? WarningAmberRoundedIcon
      : InfoRoundedIcon;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      PaperProps={{
        sx: {
          width: "min(440px, 92vw)",
          borderRadius: 3,
          boxShadow: "0 12px 32px rgba(2,6,23,.28)",
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Top large icon */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 3,
          }}
        >
          <Box
            sx={{
              width: 86,
              height: 86,
              borderRadius: "50%",
              bgcolor: bg,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Icon sx={{ fontSize: 48, color }} />
          </Box>

          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "text.secondary",
            }}
            size="small"
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Alert body (clean message) */}
        <Alert
          severity={severity}
          variant="standard"
          icon={false}
          sx={{
            borderRadius: 0,
            px: 3,
            py: 2.5,
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>
            {title || titleMap[severity] || "Notice"}
          </AlertTitle>
          {message}
        </Alert>
      </DialogContent>
    </Dialog>
  );
}
